import { useState, useEffect, useMemo } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { DIFFICULTY_TO_SIZE, ALLOWED_DIFFS, PUZZLE_IMAGE_PATHS } from '../../constants/puzzle'
import { cx } from '@utils/cx'
import styles from './PuzzleStart.module.css'

export default function PuzzleStart ({
  initialDifficulty = 'easy',
  onGameStart
}) {
  const { t } = useLanguage()

  const [difficulty, setDifficulty] = useState(
    ALLOWED_DIFFS.has(initialDifficulty) ? initialDifficulty : 'easy'
  )
  const size = DIFFICULTY_TO_SIZE[difficulty] || DIFFICULTY_TO_SIZE.easy
  const [showRules, setShowRules] = useState(false)

  const rulesGame = useMemo(() => ([
    t.puzzle.start.rulesBody1,
    t.puzzle.start.rulesBody2,
    t.puzzle.start.rulesBody3,
    t.puzzle.start.rulesBody4
  ]), [t])
  const rulesCollab = useMemo(() => ([
    t.puzzle.start.rulesBody5,
    t.puzzle.start.rulesBody6
  ]), [t])

  useEffect(() => {
    // Reserved for future side-effects (analytics, etc.)
    // Image preload: runs only when user is already in /:lang/puzzle
    // Keeps bundle initial light and smooths Start → Game transition
    const img = new window.Image()
    img.src = PUZZLE_IMAGE_PATHS.cover
    if (typeof img.decode === 'function') {
      img.decode().catch(() => {}) // graceful degradation
    }
  }, [])

  const handleStart = () => {
    if (typeof onGameStart === 'function') onGameStart(difficulty)
  }

  return (
    <section
      className={styles.puzzleStart}
      aria-label={t.puzzle.access.startRegion}
    >
      <header className={styles.header}>
        <h1 className={styles.title}>
          {t.puzzle.title}
        </h1>
      </header>

      {/* Rules / Instructions (accordion markup) */}
      <div className={styles.rules}>
        <button
          type='button'
          className={styles.rulesToggle}
          aria-expanded={showRules}
          aria-controls='puzzle-start__rules'
          onClick={() => setShowRules(prev => !prev)}
        >
          {t.puzzle.start.rulesTitle}
        </button>
        <div
          id='puzzle-start-rules'
          className={cx(
            styles.rulesPanel,
            showRules && styles.rulesPanelVisible
          )}
          aria-hidden={!showRules}
        >
          <div className={styles.rulesBody}>
            <ul className={styles.rulesList} aria-label={t.puzzle.start.rulesTitle}>
              {rulesGame.filter(Boolean).map((text, idx) => (
                <li key={`rg-${idx}`}>{text}</li>
              ))}
            </ul>
            <div className={styles.rulesSep} role='separator' aria-hidden='true' />
            <ul className={cx(styles.rulesList, styles.rulesListCollab)}>
              {rulesCollab.filter(Boolean).map((text, idx) => (
                <li key={`rc-${idx}`}>{text}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className={styles.content}>
        {/* Difficulty toggle buttons */}
        <div
          className={styles.controls}
          role='group'
          aria-label={t.puzzle.access.selector}
        >
          <button
            type='button'
            className={cx(
              styles.difficultyBtn,
              difficulty === 'easy' && styles.difficultyBtnActive
            )}
            aria-pressed={difficulty === 'easy'}
            onClick={() => setDifficulty('easy')}
          >
            {t.puzzle.start.selector.easy}
          </button>

          <button
            type='button'
            className={cx(
              styles.difficultyBtn,
              difficulty === 'medium' && styles.difficultyBtnActive
            )}
            aria-pressed={difficulty === 'medium'}
            onClick={() => setDifficulty('medium')}
          >
            {t.puzzle.start.selector.medium}
          </button>

          <button
            type='button'
            className={cx(
              styles.difficultyBtn,
              difficulty === 'hard' && styles.difficultyBtnActive
            )}
            aria-pressed={difficulty === 'hard'}
            onClick={() => setDifficulty('hard')}
          >
            {t.puzzle.start.selector.hard}
          </button>
        </div>

        {/* Mock board */}
        <div className={styles.preview} aria-hidden='true'>
          <div
            className={styles.mockBoard}
            style={{ '--puzzle-size': size }}
            aria-hidden='true'
          >
            {Array.from({ length: size * size }).map((_, i) => (
              <div key={i} className={styles.mockTile} aria-hidden='true'>
                <span className={styles.mockGlyph}>?</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type='button'
            className={styles.startBtn}
            onClick={handleStart}
          >
            {t.puzzle.start.startGame}
          </button>
        </div>
      </div>
    </section>
  )
}
